/**
 * CalendarToolbar - Toolbar customizada para o calendário de atividades
 * Navegação, título do período e toggle de visão
 * 
 * @version 1.1.0
 * RELEASE: 2026-07-24 v1.1.0 - Integra DateRangeJumpPicker (salto direto por
 *                              data em Dia/Semana/Mês). Prop `onJumpToDate`
 *                              opcional — sem quebra de compatibilidade.
 * RELEASE: 2026-02-09 v1.0.0 - Implementação inicial
 */
import React from 'react';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { DateRangeJumpPicker } from '@/components/calendar/DateRangeJumpPicker';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

// Tipos de visão do calendário
export type CalendarView = 'dayGridMonth' | 'timeGridWeek' | 'timeGridDay';

interface CalendarToolbarProps {
  /** Título do período atual (ex: "Janeiro 2026") */
  title: string;
  /** Data central atual */
  currentDate: Date;
  /** Visão atual */
  currentView: CalendarView;
  /** Callback para navegação anterior */
  onPrev: () => void;
  /** Callback para navegação próxima */
  onNext: () => void;
  /** Callback para ir para hoje */
  onToday: () => void;
  /** Callback para mudar visão */
  onViewChange: (view: CalendarView) => void;
  /** (Opcional) Callback para saltar direto a uma data via picker adaptativo. */
  onJumpToDate?: (date: Date) => void;
}

/**
 * Formata o título do período baseado na visão
 */
function formatTitle(date: Date, view: CalendarView): string {
  switch (view) {
    case 'dayGridMonth':
      return format(date, "MMMM 'de' yyyy", { locale: ptBR });
    case 'timeGridWeek':
      return format(date, "'Semana de' dd 'de' MMMM", { locale: ptBR });
    case 'timeGridDay':
      return format(date, "EEEE, dd 'de' MMMM", { locale: ptBR });
    default:
      return format(date, "MMMM 'de' yyyy", { locale: ptBR });
  }
}

/**
 * Toolbar do calendário com navegação e seletor de visão
 */
export function CalendarToolbar({
  title,
  currentDate,
  currentView,
  onPrev,
  onNext,
  onToday,
  onViewChange,
  onJumpToDate,
}: CalendarToolbarProps) {
  // Capitaliza a primeira letra do título
  const formattedTitle = title.charAt(0).toUpperCase() + title.slice(1);

  return (
    <div className="flex flex-col gap-1.5 sm:gap-3 sm:flex-row sm:items-center sm:justify-between mb-2 sm:mb-4">
      {/* Linha 1: Navegação + Título */}
      <div className="flex items-center gap-1 sm:gap-2">
        {/* Botão Hoje - compacto no mobile */}
        <Button
          variant="outline"
          size="sm"
          onClick={onToday}
          className="h-6 sm:h-8 text-[10px] sm:text-sm px-1.5 sm:px-3"
        >
          <Calendar className="h-3 w-3 sm:h-4 sm:w-4 mr-0.5 sm:mr-1" />
          Hoje
        </Button>

        {/* Navegação Anterior/Próximo */}
        <div className="flex items-center border rounded-md">
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 sm:h-8 sm:w-8 rounded-r-none"
            onClick={onPrev}
          >
            <ChevronLeft className="h-3 w-3 sm:h-4 sm:w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 sm:h-8 sm:w-8 rounded-l-none"
            onClick={onNext}
          >
            <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4" />
          </Button>
        </div>

        {/* Picker de data adaptativo (Dia/Semana/Mês) — só aparece se o pai
            forneceu `onJumpToDate` (compat com integrações antigas). */}
        {onJumpToDate && (
          <DateRangeJumpPicker
            currentDate={currentDate}
            view={currentView}
            onPick={onJumpToDate}
            className="h-6 sm:h-8 text-[10px] sm:text-xs"
          />
        )}

        {/* Título do Período */}
        <h2 className="text-xs sm:text-base md:text-lg font-semibold text-foreground ml-0.5 sm:ml-2 truncate">
          {formattedTitle}
        </h2>
      </div>

      {/* Linha 2 mobile / Direita desktop: Toggle de Visão */}
      <ToggleGroup
        type="single"
        value={currentView}
        onValueChange={(value) => {
          if (value) onViewChange(value as CalendarView);
        }}
        className="h-6 sm:h-8 self-start sm:self-auto"
      >
        <ToggleGroupItem
          value="dayGridMonth"
          aria-label="Visão Mês"
          className="h-6 sm:h-8 px-2 sm:px-3 text-[10px] sm:text-xs"
        >
          Mês
        </ToggleGroupItem>
        <ToggleGroupItem
          value="timeGridWeek"
          aria-label="Visão Semana"
          className="h-6 sm:h-8 px-2 sm:px-3 text-[10px] sm:text-xs"
        >
          Semana
        </ToggleGroupItem>
        <ToggleGroupItem
          value="timeGridDay"
          aria-label="Visão Dia"
          className="h-6 sm:h-8 px-2 sm:px-3 text-[10px] sm:text-xs"
        >
          Dia
        </ToggleGroupItem>
      </ToggleGroup>
    </div>
  );
}
